import React, {useState} from 'react';
import {
    Paper,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Collapse,
    IconButton,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import type {HKfDictionaryResult, FrsParagraphNode} from '@/services/auditService/interface/sme-frs';

export interface SmeFrsDictionaryPaperProps {
    data?: HKfDictionaryResult[] | null;
    embed?: boolean;
}

/**
 * 递归渲染段落节点
 */
const ParagraphNode: React.FC<{ node: FrsParagraphNode; depth?: number }> = ({node, depth = 0}) => {
    const hasChildren = node.children && node.children.length > 0;

    return (
        <>
            <ListItem
                sx={{
                    py: 0.1,
                    pl: 1.5 + depth * 1.5,
                    alignItems: 'flex-start',
                }}
                disableGutters
            >
                <ListItemText
                    primary={node.content}
                    primaryTypographyProps={{
                        variant: 'caption',
                        sx: {
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.4,
                            color: 'text.secondary',
                        },
                    }}
                />
            </ListItem>
            {hasChildren && node.children!.map((child, idx) => (
                <ParagraphNode key={idx} node={child} depth={depth + 1}/>
            ))}
        </>
    );
};

/**
 * SME-FRS 字典内容展示组件
 * 用于在 WorkingPaper 底部渲染准则段落内容
 */
const SmeFrsDictionaryPaper: React.FC<SmeFrsDictionaryPaperProps> = ({data, embed}) => {
    const [expanded, setExpanded] = useState(false);

    if (!data || !Array.isArray(data) || data.length === 0) {
        return null;
    }

    // 过滤掉没有段落内容的条目
    const validEntries = data.filter(item => item.frsParagraphs && item.frsParagraphs.length > 0);
    if (validEntries.length === 0) {
        return null;
    }

    // embed 模式：仅渲染内容区域，不包含 Paper 外壳与标题栏
    const contentBody = (
        <Box sx={{px: 1, py: 0.25, maxHeight: 400, overflow: 'auto'}}>
            {validEntries.map((entry, entryIdx) => (
                <Box key={entryIdx}>
                    {entryIdx > 0 && <Divider sx={{my: 1}}/>}
                    <Typography variant="caption" color="primary" sx={{fontWeight: 600}}>
                        {entry.frsSection}
                    </Typography>
                    <Typography variant="caption" sx={{color: 'text.primary', display: 'block'}}>
                        {entry.title}
                    </Typography>
                    <List dense disablePadding>
                        {entry.frsParagraphs.map((paragraph, idx) => (
                            <ParagraphNode key={idx} node={paragraph} depth={0}/>
                        ))}
                    </List>
                </Box>
            ))}
        </Box>
    );

    if (embed) {
        return contentBody;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                mt: 2,
                p: 0,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
            }}
        >
            {/* 标题栏 - 始终显示，点击展开/折叠 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 0.75,
                    bgcolor: 'action.hover',
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': {bgcolor: 'action.selected'},
                }}
                onClick={() => setExpanded(!expanded)}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <MenuBookIcon fontSize="small" color="primary"/>
                    <Typography variant="subtitle2" color="primary" sx={{fontWeight: 600}}>
                        HKFRS SME-FRS
                    </Typography>
                </Box>
                <IconButton size="small" sx={{ml: 1}}>
                    {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
            </Box>

            {/* 段落内容 - 可折叠，遍历所有字典条目 */}
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                {contentBody}
            </Collapse>
        </Paper>
    );
};

export default SmeFrsDictionaryPaper;
